import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./contactMappings.scss";
import { iUser } from "../../interfaces/iUser";
import { GetMailboxUsers } from "../../Logic/Enquiries/Queue";
import {
    AddContactMapping,
    ContactDTO,
    ContactMappingDTO,
    GetContact,
    RemoveContactMapping,
} from "../../Logic/EmsAPI/Contacts";
import { RootState } from "../../state/store";
import { iMailbox } from "../../state/userSlice";
import Confirm from "../Generic/ConfirmDialog";
import FindDropdownPersist from "../Generic/FindDropdownPersist";

function userName(user: { firstname: string | null; lastname: string | null }): string {
    return `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();
}

interface Props {
    contact: ContactDTO;
    onClose: () => void;
}

function ContactMappingModal(props: Props) {
    const permissions = useSelector((state: RootState) => state.permissions);
    const myMailboxes = useSelector((state: RootState) => state.UserInformation.mailboxes);

    const [mappings, setMappings] = useState<ContactMappingDTO[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [mailbox, setMailbox] = useState<iMailbox>();
    const [users, setUsers] = useState<iUser[]>([]);
    const [user, setUser] = useState<iUser>();
    const [confirmingRemove, setConfirmingRemove] = useState<number>();
    const [saving, setSaving] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const canMap = (mailboxID: number) =>
        permissions.Mailboxes.find(x => Number(x.ID) === mailboxID)?.Permissions.ContactMailboxMapping === true;

    // a contact has at most one mapping per mailbox, so mapped mailboxes are not offered again
    const mappableMailboxes = myMailboxes
        .filter(x => canMap(x.id) && !mappings.some(m => m.mailboxID === x.id))
        .sort((a, b) => a.name.localeCompare(b.name));

    const contactID = props.contact.id;
    const loadMappings = useCallback(async () => {
        const response = await GetContact(contactID);
        if (response.isOk && response.data) {
            setMappings(response.data.mappings);
        }
        setLoaded(true);
    }, [contactID]);

    useEffect(() => {
        loadMappings();
    }, [loadMappings]);

    const chooseMailbox = async (chosen: iMailbox) => {
        setMailbox(chosen);
        setUser(undefined);
        setUsers([]);
        const mailboxUsers = await GetMailboxUsers([chosen.id]);
        setUsers(mailboxUsers.filter(x => !x.blocked));
    };

    const showErrors = (validationErrors?: { errors?: Record<string, string[]> }) =>
        setServerErrors(validationErrors?.errors ? Object.values(validationErrors.errors).flat() : []);

    const add = async () => {
        if (!mailbox || !user || saving) return;
        setSaving(true);
        setServerErrors([]);
        const response = await AddContactMapping(props.contact.id, { mailboxID: mailbox.id, userID: user.id });
        setSaving(false);
        if (response.isOk) {
            setMailbox(undefined);
            setUser(undefined);
            setUsers([]);
            await loadMappings();
        } else {
            showErrors(response.validationErrors);
        }
    };

    const remove = async (mailboxID: number) => {
        if (saving) return;
        setSaving(true);
        setServerErrors([]);
        const response = await RemoveContactMapping(props.contact.id, mailboxID);
        setSaving(false);
        setConfirmingRemove(undefined);
        if (response.isOk) {
            await loadMappings();
        } else {
            showErrors(response.validationErrors);
        }
    };

    const actionCell = (mapping: ContactMappingDTO) => {
        if (!canMap(mapping.mailboxID)) {
            return (
                <span
                    className="view-only"
                    title="You don't have Contact Mailbox Mapping on this mailbox"
                >
                    View only
                </span>
            );
        }
        if (confirmingRemove === mapping.mailboxID) {
            return (
                <div className="confirm-remove">
                    Remove?
                    <button
                        className="g-btn g-btn-error"
                        disabled={saving}
                        onClick={() => remove(mapping.mailboxID)}
                    >
                        Remove
                    </button>
                    <button
                        className="link-button"
                        onClick={() => setConfirmingRemove(undefined)}
                    >
                        Keep
                    </button>
                </div>
            );
        }
        return (
            <button
                className="link-button danger"
                onClick={() => setConfirmingRemove(mapping.mailboxID)}
            >
                Remove
            </button>
        );
    };

    const addSection = () => {
        if (mappableMailboxes.length === 0) {
            return (
                <div className="add-mapping">
                    <p className="add-mapping-hint">
                        {permissions.AnyMailbox.ContactMailboxMapping
                            ? "Every mailbox you can map already has a mapping for this contact."
                            : "You don't have Contact Mailbox Mapping on any mailbox, so you can't add mappings."}
                    </p>
                </div>
            );
        }
        return (
            <div className="add-mapping">
                <p className="add-mapping-title">Add a mapping</p>
                <div className="add-mapping-field">
                    Mailbox
                    <FindDropdownPersist
                        id={1}
                        label={mailbox?.name ?? "Choose a mailbox"}
                    >
                        {mappableMailboxes.map(x => (
                            <li key={x.id}>
                                <button onClick={() => chooseMailbox(x)}>{x.name}</button>
                            </li>
                        ))}
                    </FindDropdownPersist>
                </div>
                <div className="add-mapping-field">
                    User
                    {mailbox ? (
                        <FindDropdownPersist
                            id={2}
                            label={user ? userName(user) : "Choose a user"}
                        >
                            {users.map(x => (
                                <li key={x.id}>
                                    <button onClick={() => setUser(x)}>{userName(x)}</button>
                                </li>
                            ))}
                        </FindDropdownPersist>
                    ) : (
                        <div className="find-dropdown-disabled">Choose a mailbox first</div>
                    )}
                </div>
                <button
                    className={`g-btn ${mailbox && user && !saving ? "g-btn-action" : "g-btn-disabled"}`}
                    disabled={!mailbox || !user || saving}
                    onClick={add}
                >
                    Add
                </button>
                <p className="add-mapping-hint">
                    Only mailboxes where you have Contact Mailbox Mapping are listed. To send this contact&apos;s mail
                    to someone else, remove the mapping and add it again.
                </p>
            </div>
        );
    };

    return (
        <Confirm
            title={`Mappings for ${props.contact.name || props.contact.emailAddress}`}
            oKText="Close"
            onConfirm={props.onClose}
            // Enter in a dropdown's search box must not close the dialog
            suppressKeyCapture={true}
        >
            <div className="contact-mappings">
                <div className="contact-mappings-email">{props.contact.emailAddress}</div>
                <div>New emails from this contact are assigned to:</div>
                {serverErrors.length > 0 && (
                    <div
                        className="server-errors"
                        role="alert"
                    >
                        {serverErrors.map(message => (
                            <div key={message}>{message}</div>
                        ))}
                    </div>
                )}
                <table>
                    <thead>
                        <tr>
                            <th>Mailbox</th>
                            <th>Assigned to</th>
                            <th className="mapping-action">
                                <span className="visually-hidden">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loaded && mappings.length === 0 && (
                            <tr className="no-mappings">
                                <td colSpan={3}>
                                    No mappings yet. New emails from this contact go to each mailbox&apos;s unassigned
                                    queue.
                                </td>
                            </tr>
                        )}
                        {mappings.map(mapping => (
                            <tr
                                key={mapping.mailboxID}
                                className={confirmingRemove === mapping.mailboxID ? "confirming" : ""}
                            >
                                <td>
                                    {mapping.mailboxName}
                                    <span className="mailbox-address">{mapping.mailboxEmailAddress}</span>
                                </td>
                                <td>{mapping.user ? userName(mapping.user) : ""}</td>
                                <td className="mapping-action">{actionCell(mapping)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {loaded && addSection()}
            </div>
        </Confirm>
    );
}
export default ContactMappingModal;
